import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { includesQuery, normalizedQuery } from "#/lib/search";

import type { BookView } from "#/lib/bookling-data";

export type SortMode = "mentions" | "title" | "author";

const SORT_OPTIONS: ReadonlyArray<{ value: SortMode; label: string }> = [
  { value: "mentions", label: "Mentions" },
  { value: "title", label: "Title" },
  { value: "author", label: "Author" },
];

type BookLibraryProps = {
  books: ReadonlyArray<BookView>;
};

export function BookLibrary({ books }: BookLibraryProps) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("mentions");

  const filtered = useMemo(() => {
    const q = normalizedQuery(query);
    const base = q
      ? books.filter((b) => includesQuery(b.title, q) || includesQuery(b.author, q))
      : books;

    const sorted = [...base];
    if (sort === "mentions") {
      sorted.sort((a, b) => b.mentionCount - a.mentionCount || a.title.localeCompare(b.title));
    } else if (sort === "title") {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    } else {
      sorted.sort((a, b) => a.author.localeCompare(b.author) || a.title.localeCompare(b.title));
    }
    return sorted;
  }, [books, query, sort]);

  return (
    <>
      <div className="library-toolbar library-toolbar--split">
        <label className="library-search library-search--pill">
          <Search className="library-search-icon h-4 w-4" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title or author"
            aria-label="Search books"
          />
        </label>

        <div
          className="sort-control sort-control--pill"
          role="radiogroup"
          aria-label="Sort books"
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={sort === opt.value}
              className={sort === opt.value ? "sort-pill is-active" : "sort-pill"}
              onClick={() => setSort(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="library-empty">No books match that search.</p>
      ) : (
        <ul className="shelf-grid">
          {filtered.map((book, index) => (
            <BookTile key={book.id} book={book} index={index} />
          ))}
        </ul>
      )}
    </>
  );
}

function BookTile({ book, index }: { book: BookView; index: number }) {
  return (
    <li
      className="shelf-tile"
      style={
        {
          "--tile-delay": `${Math.min(index, 24) * 18}ms`,
        } as React.CSSProperties
      }
    >
      <Link
        to="/books/$slug"
        params={{ slug: book.slug }}
        className="shelf-tile-link"
        aria-label={`${book.title} by ${book.author}`}
      >
        <div className="shelf-cover">
          {book.imageUrl ? (
            <img src={book.imageUrl} alt={`${book.title} cover`} loading="lazy" decoding="async" />
          ) : (
            <div className="shelf-cover-placeholder" aria-hidden="true">
              <span>{book.title.slice(0, 1)}</span>
            </div>
          )}
          {book.mentionCount > 1 ? (
            <span className="shelf-badge" title={`${book.mentionCount} mentions`}>
              {book.mentionCount}
            </span>
          ) : null}
        </div>
        <h3 className="shelf-title" title={`${book.title} - ${book.author}`}>
          {book.title}
        </h3>
      </Link>
    </li>
  );
}
